import { ArrowUturnLeftIcon } from "@heroicons/react/24/outline";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { readSegment } from "../../../api/content/sourcesApi";
import { appPaths } from "../../../shared/routing/appPaths";
import { useLoad } from "../../../shared/loading/useLoad";
import type { DocumentVersionRef } from "../../../shared/references/contentReferences";
import AppButton from "../../../ui/buttons/AppButton";
import CopyReference from "../../../ui/content/CopyReference";
import Chip from "../../../ui/feedback/Chip";
import LoadStatus from "../../../ui/feedback/LoadStatus";

interface SegmentFocusProps {
  document: DocumentVersionRef;
  segmentPath: string;
}

/**
 * The segment a segment reference names, opened from the server by that reference
 * (FR-CORPUS-4), with a way back to the whole document. Work on it starts from the
 * document header's one action (F27).
 */
export default function SegmentFocus({ document, segmentPath }: SegmentFocusProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { documentId, version } = document;
  const load = useCallback(
    (signal: AbortSignal) => readSegment({ documentId, version }, segmentPath, signal),
    [documentId, version, segmentPath],
  );
  const { state: segment, reload } = useLoad(load);
  const title = t("sources.segment.title", { segmentPath });

  return (
    <section className="app-card mx-4 space-y-2 p-4 sm:mx-0" aria-label={title}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <h3 className="app-title text-base font-semibold">{title}</h3>
          {segment.status === "loaded" ? (
            <>
              <Chip>{segment.value.segmentType}</Chip>
              <CopyReference value={segment.value.reference} />
            </>
          ) : null}
        </div>
        <AppButton variant="ghost" size="sm" className="shrink-0" onClick={() => void navigate(appPaths.sourceDocument({ documentId, version }), { replace: true })}>
          <ArrowUturnLeftIcon className="h-4 w-4" aria-hidden="true" />
          {t("sources.segment.wholeDocument")}
        </AppButton>
      </div>
      <LoadStatus state={segment} loadingLabel={t("common.loading")} onRetry={reload} compact />
      {segment.status === "loaded" ? (
        <p className="app-text whitespace-pre-line text-sm">{segment.value.text}</p>
      ) : null}
    </section>
  );
}
